import type { Channel, Program } from "@hybrid-iptv/types";
import { withinCatchupWindow } from "@hybrid-iptv/iptv-parser";

/**
 * Section 16 step 4 — the "virtual DVR": browsing catch-up as a page of its own
 * rather than only finding it by scrolling the guide backwards.
 *
 * The rules live here, apart from the page, because they are date arithmetic and
 * deserve tests: which channels have an archive, how far back it goes, and which
 * programmes on a given day have both FINISHED and are still inside the window.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * How many days back to offer when the provider advertises an archive but does
 * not say how long it keeps (`tv_archive_duration` absent). Only the BROWSE
 * depth: playback is still gated by `withinCatchupWindow`, which treats an
 * unstated duration as unbounded rather than guessing on the viewer's behalf.
 */
export const DEFAULT_BROWSE_DAYS = 7;

/** The channels whose provider keeps an archive, in the catalog's own order. */
export function archiveChannels(channels: Channel[]): Channel[] {
  return channels.filter((c) => c.catchupAvailable === true);
}

export interface CatchupDay {
  /** Local midnight that starts the day. */
  startMs: number;
  /** "Today", "Yesterday", or a weekday and date. */
  label: string;
}

function midnight(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/**
 * The days a channel can be browsed, most recent first. Day zero is today, and
 * the list stops at the channel's advertised depth — a 3-day archive offers
 * today plus the three days behind it, because part of the oldest day is still
 * inside the window.
 */
export function catchupDayList(channel: Pick<Channel, "catchupDays">, nowMs: number): CatchupDay[] {
  const depth = channel.catchupDays ?? DEFAULT_BROWSE_DAYS;
  const today = midnight(nowMs);
  const days: CatchupDay[] = [];
  for (let back = 0; back <= depth; back += 1) {
    const startMs = midnight(today - back * DAY_MS);
    days.push({ startMs, label: dayLabel(startMs, today) });
  }
  return days;
}

function dayLabel(startMs: number, todayMs: number): string {
  const back = Math.round((todayMs - startMs) / DAY_MS);
  if (back === 0) return "Today";
  if (back === 1) return "Yesterday";
  return new Date(startMs).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
}

/**
 * The replayable programmes of one day on one channel, most recent first.
 *
 * A programme qualifies only when it has ENDED — one still airing is live, and
 * "watch from the start" is the guide's job — and when its start is still inside
 * the provider's window, so the page never offers a row the panel will 404.
 */
export function catchupProgramsOn(
  channel: Pick<Channel, "catchupDays">,
  programs: Program[],
  dayStartMs: number,
  nowMs: number,
): Program[] {
  const dayEndMs = dayStartMs + DAY_MS;
  return programs
    .filter((p) => {
      const startMs = new Date(p.start).getTime();
      const endMs = new Date(p.end).getTime();
      if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return false;
      if (startMs < dayStartMs || startMs >= dayEndMs) return false;
      if (endMs > nowMs) return false;
      return withinCatchupWindow(channel, { startMs, endMs }, nowMs);
    })
    .sort((a, b) => b.start.localeCompare(a.start));
}

/** "2:05 PM – 2:55 PM", the row's own clock. */
export function programClock(program: Pick<Program, "start" | "end">): string {
  const fmt = (iso: string) =>
    new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `${fmt(program.start)} – ${fmt(program.end)}`;
}
